import { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { Trophy, Crown, Medal, Flag, Timer, Zap, TrendingUp, Flame, Coins, Download, Share2, Info, Search } from 'lucide-react';
import { toPng } from 'html-to-image';
import confetti from 'canvas-confetti';
import { Button } from '../ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const useCountdown = (targetIso) => {
    const [left, setLeft] = useState(0);
    useEffect(() => {
        if (!targetIso) return;
        const tick = () => setLeft(Math.max(0, new Date(targetIso).getTime() - Date.now()));
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [targetIso]);
    const s = Math.floor(left / 1000);
    return {
        d: Math.floor(s / 86400),
        h: Math.floor((s % 86400) / 3600),
        m: Math.floor((s % 3600) / 60),
        s: s % 60,
        done: left <= 0,
    };
};

const RANK_ICON = [Crown, Medal, Flag];
const RANK_COLOR = ['#FFD700', '#C0C0C0', '#CD7F32'];

const CountBox = ({ v, label }) => (
    <div className="flex flex-col items-center">
        <span className="font-mono font-black text-2xl sm:text-3xl text-white tabular-nums" data-testid={`cup-count-${label}`}>
            {String(v).padStart(2, '0')}
        </span>
        <span className="text-[9px] uppercase tracking-widest text-[#A0AAB2]">{label}</span>
    </div>
);

export const AmbassadorCupCard = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const recapRef = useRef(null);
    const [sharing, setSharing] = useState(false);
    const [showHelp, setShowHelp] = useState(false);
    const [lbQuery, setLbQuery] = useState('');
    const [lbExpanded, setLbExpanded] = useState(false);
    const confettiFiredRef = useRef(false);

    const load = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/ambassador/cup`, { withCredentials: true });
            setData(data);
        } catch {
            setData({ competition: null });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        const id = setInterval(load, 30000);
        return () => clearInterval(id);
    }, [load]);

    const comp = data?.competition;
    const cd = useCountdown(comp?.status === 'scheduled' ? comp?.start_at : comp?.end_at);

    // One celebratory burst when the Cup is shown as finalized.
    useEffect(() => {
        if (comp?.status === 'finalized' && !confettiFiredRef.current) {
            confettiFiredRef.current = true;
            try {
                confetti({ particleCount: 140, spread: 75, origin: { y: 0.3 },
                    colors: ['#FFD700', '#39FF14', '#A855F7', '#FFFFFF'] });
            } catch { /* no-op */ }
        }
    }, [comp?.status]);

    if (loading) {
        return <div className="rounded-xl border border-zinc-800 bg-[#121212] p-6 text-center text-zinc-500" data-testid="cup-loading">Loading the Cup…</div>;
    }

    if (!comp) {
        return (
            <div className="rounded-xl border border-zinc-800 bg-[#121212] p-8 text-center" data-testid="cup-none">
                <Trophy className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
                <p className="text-zinc-300 font-semibold">No active competition right now</p>
                <p className="text-zinc-500 text-sm mt-1">Keep referring — when the next Cup drops, every commission dollar counts toward the prize pool.</p>
            </div>
        );
    }

    const me = data.me || {};
    const rows = data.rows || [];
    const myAwards = data.my_awards || [];
    const milestones = comp.milestones || [];
    const nextMilestone = milestones.find((m) => me.score < m.threshold);
    const topMilestone = milestones.length ? milestones[milestones.length - 1].threshold : 0;
    const progressPct = topMilestone > 0 ? Math.min(100, (me.score / topMilestone) * 100) : 0;

    // Leaderboard view: filter by name search, else collapse to top 10 (always keep "you" visible).
    const LB_COLLAPSE = 10;
    const q = lbQuery.trim().toLowerCase();
    const lbFiltered = q ? rows.filter((r) => (r.name || '').toLowerCase().includes(q)) : rows;
    const lbCollapsed = !q && !lbExpanded && lbFiltered.length > LB_COLLAPSE;
    let lbVisible = lbCollapsed ? lbFiltered.slice(0, LB_COLLAPSE) : lbFiltered;
    const meInVisible = lbVisible.some((r) => r.ambassador_id === me.ambassador_id);
    const myRow = rows.find((r) => r.ambassador_id === me.ambassador_id);
    const showMyPin = lbCollapsed && !meInVisible && myRow;

    const statusBadge = {
        running: { t: 'LIVE NOW', c: '#39FF14' },
        scheduled: { t: 'STARTS SOON', c: '#00E0FF' },
        ended: { t: 'ENDED', c: '#FFD700' },
        finalized: { t: 'FINAL RESULTS', c: '#A855F7' },
        paused: { t: 'PAUSED', c: '#A0AAB2' },
    }[comp.status] || { t: '', c: '#A0AAB2' };

    const podium = rows.slice(0, 3);
    const myPrize = myAwards.reduce((a, b) => a + (b.amount || 0), 0);
    const pool = data.prize_pool || {};

    const FireBadge = ({ row }) => (row?.on_fire ? (
        <span className="inline-flex items-center gap-0.5 text-[10px] font-black px-1.5 py-0.5 rounded-full bg-[#FF6A00]/15 text-[#FF8C33] border border-[#FF6A00]/40 align-middle"
            title={`On fire — ${row.streak} days earning in a row`} data-testid={`cup-fire-${row.rank}`}>
            <Flame className="w-3 h-3" />{row.streak}
        </span>
    ) : null);

    const buildRecapImage = async () => {
        if (!recapRef.current) return null;
        return await toPng(recapRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: '#0a0a0a' });
    };
    const downloadRecap = async () => {
        setSharing(true);
        try {
            const url = await buildRecapImage();
            if (!url) return;
            const a = document.createElement('a');
            a.href = url; a.download = 'ambassador-cup-recap.png'; a.click();
        } catch { /* no-op */ } finally { setSharing(false); }
    };
    const shareRecap = async () => {
        setSharing(true);
        try {
            const url = await buildRecapImage();
            if (!url) return;
            const blob = await (await fetch(url)).blob();
            const file = new File([blob], 'ambassador-cup-recap.png', { type: 'image/png' });
            const text = `I finished #${me.rank} in the ${comp.name}${myPrize > 0 ? ` and won ${fmt(myPrize)}` : ''}! 🏆`;
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({ files: [file], text, title: comp.name });
            } else {
                const a = document.createElement('a');
                a.href = url; a.download = 'ambassador-cup-recap.png'; a.click();
            }
        } catch { /* user cancelled or unsupported */ } finally { setSharing(false); }
    };

    return (
        <div className="space-y-4" data-testid="cup-card">
            {/* Banner */}
            <div className="rounded-xl p-5 border border-[#FFD700]/40 bg-gradient-to-br from-[#FFD700]/10 via-[#A855F7]/5 to-transparent relative overflow-hidden">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                        <Trophy className="w-6 h-6 text-[#FFD700]" />
                        <h3 className="text-xl font-black text-white">{comp.name}</h3>
                    </div>
                    <span className="text-[10px] font-black px-2 py-0.5 rounded uppercase tracking-widest"
                        style={{ color: statusBadge.c, background: `${statusBadge.c}1a`, border: `1px solid ${statusBadge.c}55` }}
                        data-testid="cup-status">
                        {statusBadge.t}
                    </span>
                </div>

                {/* Prize Pool Ticker — total cash at stake, climbs as milestones are hit */}
                {pool.total > 0 && (
                    <div className="mt-3 flex items-end gap-3 flex-wrap" data-testid="cup-prize-pool">
                        <div className="flex items-center gap-2">
                            <Coins className="w-5 h-5 text-[#FFD700]" />
                            <div>
                                <p className="text-[9px] uppercase tracking-widest text-[#A0AAB2] leading-none mb-0.5">Prize pool up for grabs</p>
                                <p className="text-3xl sm:text-4xl font-black font-mono text-[#FFD700] leading-none" data-testid="cup-pool-total">
                                    {fmt(pool.total)}
                                </p>
                            </div>
                        </div>
                        <p className="text-[10px] text-[#A0AAB2] mb-0.5">
                            {fmt(pool.rank_pool)} in rank prizes
                            {pool.milestone_locked > 0 ? ` + ${fmt(pool.milestone_locked)} in milestone bonuses already unlocked` : ''}
                        </p>
                    </div>
                )}

                {comp.status === 'finalized' ? (
                    <div className="mt-4 text-center py-2" data-testid="cup-finalized-banner">
                        <p className="text-sm text-[#A855F7] font-bold uppercase tracking-wider">Competition complete</p>
                        {myPrize > 0 ? (
                            <p className="text-3xl font-black text-[#39FF14] font-mono mt-1" data-testid="cup-my-prize">
                                You won {fmt(myPrize)}!
                            </p>
                        ) : (
                            <p className="text-zinc-400 text-sm mt-1">No prize this round — go again next Cup.</p>
                        )}
                    </div>
                ) : (
                    <div className="mt-4 flex items-center gap-4">
                        <Timer className="w-5 h-5 text-[#FFD700]" />
                        <div className="flex items-center gap-3 sm:gap-5">
                            <CountBox v={cd.d} label="days" />
                            <CountBox v={cd.h} label="hrs" />
                            <CountBox v={cd.m} label="min" />
                            <CountBox v={cd.s} label="sec" />
                        </div>
                        <span className="text-[11px] text-[#A0AAB2] ml-auto hidden sm:block">
                            {comp.status === 'scheduled' ? 'until it starts' : 'until the whistle'}
                        </span>
                    </div>
                )}
            </div>

            {/* Cup Recap Card — shareable summary image (finalized only) */}
            {comp.status === 'finalized' && (
                <div data-testid="cup-recap">
                    <div ref={recapRef}
                        className="rounded-2xl p-6 border border-[#FFD700]/40 bg-gradient-to-br from-[#1a1407] via-[#120a1e] to-[#0a0a0a] text-center mx-auto"
                        style={{ maxWidth: 560 }}>
                        <p className="text-[10px] tracking-[0.3em] text-[#FFD700] uppercase font-bold">BETohalics · Ambassador Cup</p>
                        <h3 className="text-2xl font-black text-white mt-1">{comp.name}</h3>
                        <p className="text-[11px] uppercase tracking-widest text-[#A855F7] font-bold mt-1">Final Results</p>
                        <div className="my-5">
                            <p className="text-[10px] uppercase tracking-widest text-zinc-500">You finished</p>
                            <p className="text-6xl font-black text-white leading-none mt-1">#{me.rank}</p>
                            <p className="text-zinc-400 text-sm mt-1">of {rows.length} ambassadors</p>
                        </div>
                        <div className="flex justify-center gap-10">
                            <div>
                                <p className="text-[10px] uppercase tracking-widest text-zinc-500">Earned</p>
                                <p className="text-2xl font-black font-mono text-[#39FF14]">{fmt(me.score)}</p>
                            </div>
                            {myPrize > 0 && (
                                <div>
                                    <p className="text-[10px] uppercase tracking-widest text-zinc-500">Prize won</p>
                                    <p className="text-2xl font-black font-mono text-[#FFD700]">{fmt(myPrize)}</p>
                                </div>
                            )}
                        </div>
                        {podium.length > 0 && (
                            <div className="mt-5 flex justify-center gap-4 flex-wrap text-[12px] text-zinc-300">
                                {podium.map((r, i) => (
                                    <span key={r.ambassador_id}>{['🥇', '🥈', '🥉'][i]} {r.name}</span>
                                ))}
                            </div>
                        )}
                        <p className="text-[11px] text-zinc-500 mt-5">
                            {myPrize > 0 ? 'Paid straight to my balance 💸' : 'Running it back next Cup 💪'}
                        </p>
                    </div>
                    <div className="flex justify-center gap-2 mt-3">
                        <Button onClick={shareRecap} disabled={sharing}
                            className="bg-[#FFD700] text-black hover:bg-[#FFD700]/80 font-bold" data-testid="cup-recap-share">
                            <Share2 className="w-4 h-4 mr-1" /> {sharing ? 'Working…' : 'Share'}
                        </Button>
                        <Button onClick={downloadRecap} disabled={sharing} variant="outline"
                            className="border-zinc-700 text-zinc-200 hover:bg-zinc-800" data-testid="cup-recap-download">
                            <Download className="w-4 h-4 mr-1" /> Download image
                        </Button>
                    </div>
                </div>
            )}

            {/* My position */}
            <div className="rounded-xl border border-zinc-800 bg-[#121212] p-5" data-testid="cup-my-position">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Your rank</p>
                        <p className="text-4xl font-black text-white" data-testid="cup-my-rank">
                            #{me.rank}
                            <span className="text-sm text-zinc-500 font-semibold ml-2">of {rows.length || 1}</span>
                        </p>
                    </div>
                    <div className="text-right">
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Commission earned</p>
                        <p className="text-3xl font-black font-mono text-[#39FF14]" data-testid="cup-my-score">{fmt(me.score)}</p>
                    </div>
                </div>

                {comp.status !== 'finalized' && me.rank > 1 && me.gap_to_next > 0 && (
                    <div className="mt-3 flex items-center gap-2 text-sm text-[#FFD700] font-semibold" data-testid="cup-gap">
                        <TrendingUp className="w-4 h-4" />
                        Just {fmt(me.gap_to_next)} behind #{me.rank - 1} — close the gap!
                    </div>
                )}

                {/* Milestone progress */}
                {milestones.length > 0 && comp.status !== 'finalized' && (
                    <div className="mt-4" data-testid="cup-milestones">
                        <div className="flex items-center justify-between text-[11px] mb-1">
                            <span className="text-zinc-400 font-semibold flex items-center gap-1">
                                <Zap className="w-3 h-3 text-[#39FF14]" /> Milestone bonuses
                            </span>
                            {nextMilestone ? (
                                <span className="text-[#39FF14]">
                                    {fmt(nextMilestone.threshold - me.score)} to go for a {fmt(nextMilestone.bonus)} bonus
                                </span>
                            ) : (
                                <span className="text-[#39FF14] font-bold">All milestones unlocked 🔥</span>
                            )}
                        </div>
                        <div className="h-2.5 rounded-full bg-white/10 overflow-hidden relative">
                            <div className="h-full rounded-full bg-gradient-to-r from-[#39FF14] to-[#FFD700] transition-all"
                                style={{ width: `${progressPct}%` }} />
                        </div>
                        <div className="flex gap-3 mt-2 flex-wrap">
                            {milestones.map((m, i) => {
                                const hit = (me.milestones_hit || []).includes(m.threshold);
                                return (
                                    <span key={i}
                                        className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${hit ? 'bg-[#39FF14]/15 text-[#39FF14] border-[#39FF14]/40' : 'bg-white/5 text-zinc-500 border-white/10'}`}
                                        data-testid={`cup-milestone-${i}`}>
                                        {hit ? '✓ ' : ''}{fmt(m.bonus)} @ {fmt(m.threshold)}
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Podium */}
            {podium.length > 0 && (
                <div className="grid grid-cols-3 gap-2" data-testid="cup-podium">
                    {podium.map((r) => {
                        const Icon = RANK_ICON[r.rank - 1];
                        const prize = (comp.prizes || []).find((p) => p.rank === r.rank);
                        const isMe = r.ambassador_id === me.ambassador_id;
                        return (
                            <div key={r.ambassador_id}
                                className={`rounded-xl border p-3 text-center ${isMe ? 'border-[#39FF14]/60 bg-[#39FF14]/5' : 'border-zinc-800 bg-[#121212]'}`}
                                data-testid={`cup-podium-${r.rank}`}>
                                <Icon className="w-5 h-5 mx-auto" style={{ color: RANK_COLOR[r.rank - 1] }} />
                                <p className="text-white text-xs font-bold truncate mt-1">
                                    {r.name} <FireBadge row={r} />
                                </p>
                                <p className="font-mono font-black text-sm text-[#39FF14]">{fmt(r.score)}</p>
                                {prize && prize.amount > 0 && (
                                    r.score > 0 ? (
                                        <p className="text-[10px] text-[#FFD700] font-bold mt-0.5">{fmt(prize.amount)} prize</p>
                                    ) : (
                                        <p className="text-[10px] text-zinc-500 font-semibold mt-0.5">{fmt(prize.amount)} up for grabs</p>
                                    )
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Full leaderboard */}
            <div className="rounded-xl border border-zinc-800 bg-[#121212] p-4" data-testid="cup-leaderboard">
                <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Leaderboard</p>
                    <button type="button" onClick={() => setShowHelp((v) => !v)}
                        className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#A855F7] hover:text-[#C77DFF] transition-colors"
                        aria-expanded={showHelp} data-testid="cup-help-toggle">
                        <Info className="w-3 h-3" /> How it's calculated
                    </button>
                </div>
                {showHelp && (
                    <div className="mb-3 rounded-lg border border-[#A855F7]/30 bg-[#A855F7]/5 p-3 text-[11px] leading-relaxed text-zinc-300"
                        data-testid="cup-help-panel">
                        <p className="text-white font-bold mb-1">Your score = rev-share on your players' net losses</p>
                        <p>
                            Every time a player you referred loses more than they win, the house keeps that
                            profit — and you earn your commission rate on it. Each payout is added to your score
                            for this month's Cup.
                        </p>
                        <p className="mt-1.5 text-zinc-400">
                            Example: a player you brought in nets the house <span className="text-white font-semibold">$10</span> and
                            your rate is <span className="text-white font-semibold">50%</span> → you earn <span className="text-[#39FF14] font-semibold">$5</span> toward your rank.
                        </p>
                        <p className="mt-1.5 text-zinc-500">
                            Only commission earned inside this Cup's dates counts here. Your all-time balance
                            (cashable on PAY DAY) can be higher.
                        </p>
                    </div>
                )}
                {rows.length === 0 ? (
                    <p className="text-sm text-zinc-500 italic py-3 text-center">No one on the board yet — be the first to put up points.</p>
                ) : (
                    <>
                        {rows.length > LB_COLLAPSE && (
                            <div className="relative mb-2">
                                <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
                                <input
                                    type="text"
                                    value={lbQuery}
                                    onChange={(e) => setLbQuery(e.target.value)}
                                    placeholder="Search ambassadors…"
                                    className="w-full bg-[#0a0a0a] border border-zinc-800 rounded-lg pl-8 pr-3 py-1.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#A855F7]/50"
                                    data-testid="cup-lb-search" />
                            </div>
                        )}
                        {lbFiltered.length === 0 ? (
                            <p className="text-sm text-zinc-500 italic py-3 text-center">No ambassadors match "{lbQuery}".</p>
                        ) : (
                            <div className="space-y-1">
                                {lbVisible.map((r) => {
                                    const isMe = r.ambassador_id === me.ambassador_id;
                                    return (
                                        <div key={r.ambassador_id}
                                            className={`flex items-center gap-3 py-1.5 px-2 rounded ${isMe ? 'bg-[#39FF14]/10 border border-[#39FF14]/30' : 'bg-white/[0.02]'}`}
                                            data-testid={`cup-lb-row-${r.rank}`}>
                                            <span className="w-6 text-center font-black text-sm"
                                                style={{ color: RANK_COLOR[r.rank - 1] || '#A0AAB2' }}>#{r.rank}</span>
                                            <span className={`text-sm flex-1 truncate ${isMe ? 'text-[#39FF14] font-bold' : 'text-white'}`}>
                                                {r.name}{isMe ? ' (you)' : ''} <FireBadge row={r} />
                                            </span>
                                            <span className="font-mono font-black text-sm text-zinc-200">{fmt(r.score)}</span>
                                        </div>
                                    );
                                })}
                                {showMyPin && (
                                    <>
                                        <div className="text-center text-zinc-600 text-xs py-0.5">· · ·</div>
                                        <div className="flex items-center gap-3 py-1.5 px-2 rounded bg-[#39FF14]/10 border border-[#39FF14]/30"
                                            data-testid={`cup-lb-row-${myRow.rank}`}>
                                            <span className="w-6 text-center font-black text-sm" style={{ color: '#A0AAB2' }}>#{myRow.rank}</span>
                                            <span className="text-sm flex-1 truncate text-[#39FF14] font-bold">{myRow.name} (you) <FireBadge row={myRow} /></span>
                                            <span className="font-mono font-black text-sm text-zinc-200">{fmt(myRow.score)}</span>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                        {!q && lbFiltered.length > LB_COLLAPSE && (
                            <button type="button" onClick={() => setLbExpanded((v) => !v)}
                                className="w-full mt-2 py-1.5 text-xs font-semibold text-[#A855F7] hover:text-[#C77DFF] transition-colors"
                                data-testid="cup-lb-toggle">
                                {lbExpanded ? 'Show top 10' : `Show all ${lbFiltered.length}`}
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default AmbassadorCupCard;
